import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { Send, X, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import apiClient from '../../utils/apiClient';

/**
 * IdentitySessionModal - Chat-like interface for guided identity extraction.
 * 
 * Philosophy:
 * - Slow, non-interrogative conversation
 * - 10-15 minutes, not seeking completion
 * - User explains themselves to their future self
 */
export function IdentitySessionModal({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [phase, setPhase] = useState('intro');
  const [status, setStatus] = useState('active');
  const [extractedData, setExtractedData] = useState({});
  
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Start or resume session when modal opens
  useEffect(() => {
    if (open) {
      startOrResumeSession();
    }
  }, [open]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const startOrResumeSession = async () => {
    try {
      setIsLoading(true);
      
      // Check for active session first
      const activeRes = await apiClient.get('/identity-session/active/current');
      
      if (activeRes.data.has_active_session) {
        // Resume existing session
        const sessionRes = await apiClient.get(`/identity-session/${activeRes.data.session.session_id}`);
        setSessionId(activeRes.data.session.session_id);
        setMessages(sessionRes.data.messages || []);
        setPhase(sessionRes.data.current_phase);
        setStatus(sessionRes.data.status);
        setExtractedData(sessionRes.data.extracted_identity || {});
      } else {
        // Start new session - PASS CURRENT LANGUAGE
        // This ensures the session uses the user's selected UI language
        const res = await apiClient.post('/identity-session/start', {
          language: i18n.language  // Pass current UI language to backend
        });
        setSessionId(res.data.session_id);
        setMessages([{
          role: 'assistant',
          content: res.data.message,
          phase: res.data.phase
        }]);
        setPhase(res.data.phase);
        setStatus('active');
        setExtractedData({});
      }
    } catch (error) {
      console.error('Failed to start identity session:', error);
      toast.error(t('identitySession.errorStarting', 'Failed to start session'));
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !sessionId || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to UI immediately
    setMessages(prev => [...prev, {
      role: 'user',
      content: userMessage,
      phase
    }]);

    setIsLoading(true);

    try {
      const res = await apiClient.post(`/identity-session/${sessionId}/message`, {
        message: userMessage
      });

      // Add AI response
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: res.data.response,
        phase: res.data.phase
      }]);

      setPhase(res.data.phase);
      setStatus(res.data.status);
      setExtractedData(res.data.initial_signals || {});

      // PHILOSOPHICAL LOCK: identity_seed_ready, not "completed"
      if (res.data.identity_seed_ready) {
        toast.success(t('identitySession.seedReady', 'This is enough to begin'));
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(t('identitySession.errorSending', 'Failed to send message'));
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
      setInputValue(userMessage);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const completeSession = async (setTone = false) => {
    if (!sessionId) return;

    try {
      setIsLoading(true);
      // PHILOSOPHICAL LOCK #3: Only tone can be set, nothing else
      await apiClient.post(`/identity-session/${sessionId}/complete`, {
        set_tone_tendency: setTone
      });

      // PHILOSOPHICAL LOCK #1: Light message, no summary
      toast.success(t('identitySession.seedReady', 'This is enough to begin'));
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to complete session:', error);
      toast.error(t('identitySession.errorCompleting', 'Failed to complete session'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getPhaseLabel = (p) => {
    const labels = {
      intro: t('identitySession.phases.intro', 'البداية'),
      values: t('identitySession.phases.values', 'القيم'),
      red_lines: t('identitySession.phases.redLines', 'الحدود'),
      priorities: t('identitySession.phases.priorities', 'الأولويات'),
      style: t('identitySession.phases.style', 'الأسلوب'),
      closing: t('identitySession.phases.closing', 'الختام')
    };
    return labels[p] || p;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl h-[80vh] flex flex-col p-0 gap-0"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold">
              {t('identitySession.title', 'جلسة الهوية')}
            </DialogTitle>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                {getPhaseLabel(phase)}
              </span>
            </div>
          </div>
        </DialogHeader>

        {/* Messages */}
        <ScrollArea 
          className="flex-1 px-6 py-4"
          ref={scrollRef}
        >
          <div className="space-y-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-gray-100 text-gray-900'
                      : 'bg-[hsl(25,95%,53%)]/10 text-gray-900 border border-[hsl(25,95%,53%)]/20'
                  }`}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-end">
                <div className="bg-gray-100 px-4 py-3 rounded-2xl">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input area */}
        {status === 'active' ? (
          <div className="px-6 py-4 border-t shrink-0">
            <div className="flex gap-3">
              <Textarea
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={t('identitySession.placeholder', 'اكتب ما يدور في ذهنك...')}
                className="flex-1 min-h-[60px] max-h-[120px] resize-none text-sm"
                disabled={isLoading}
              />
              <Button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                size="icon"
                className="shrink-0 h-[60px] w-[60px] rounded-xl bg-[hsl(25,95%,53%)] hover:bg-[hsl(25,95%,45%)]"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-4 border-t shrink-0">
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => completeSession(true)}
                className="gap-2"
                disabled={isLoading}
              >
                <Check className="w-4 h-4" />
                {t('identitySession.saveAndClose', 'حفظ وإغلاق')}
              </Button>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                {t('common.close', 'إغلاق')}
              </Button>
            </div>
          </div>
        )}

        {/* Signals preview - very subtle, not emphasized */}
        {extractedData && (
          (extractedData.values?.length > 0 || extractedData.red_lines?.length > 0)
        ) && (
          <div className="px-6 py-2 border-t bg-gray-50 text-xs text-gray-400">
            <span>{t('identitySession.signalsCaptured', 'إشارات أولية')}: </span>
            {extractedData.values?.length > 0 && (
              <span>{extractedData.values.length} {t('identitySession.values', 'قيم')}</span>
            )}
            {extractedData.red_lines?.length > 0 && (
              <span className="ms-2">{extractedData.red_lines.length} {t('identitySession.redLines', 'حدود')}</span>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default IdentitySessionModal;
